import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ConfiguratorDatastoreService, DatastoreDto, Partner} from '@tj/tj-dmp-lib-api';


@Component({
    selector: 'dmp-group-partner-selector',
    templateUrl: './group-partner-selector.component.html',
    styleUrls: ['./group-partner-selector.component.scss'],
    standalone: false
})
export class GroupPartnerSelectorComponent implements OnInit, OnChanges {
    @Input() group?: DatastoreDto;
    datastores: DatastoreDto[];
    partner?: Partner | Partner[];

    @Input() selectPartner = true;
    @Input() selectGroup = true;
    @Input() onlyInput = false;
    @Input() isMultiplePartner = false;
    @Input() isDisabled = false;
    @Input() readOnly = false;
    // Par défaut la barre ne porte pas de titre : ses segments sont déjà libellés, un
    // « Choix du groupe » au-dessus ferait doublon. Les pages qui en veulent un le demandent.
    @Input() withTitle = false;
    @Input() saveStorage = true;
    @Input() ignoreStorage = false;
    @Input() isColumn = false;
    @Input() valueGroup: string;
    @Output() valueGroupChange = new EventEmitter<string>();
    @Input() valuePartner: string | Array<string>;
    @Output() valuePartnerChange = new EventEmitter<string | string[]>();
    @Output() onGroupChange = new EventEmitter<DatastoreDto>();
    @Output() onPartnerChange = new EventEmitter<Partner | Partner[]>();
    @Output() onChange = new EventEmitter();

    constructor(
        protected datastoreResourceService: ConfiguratorDatastoreService
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.valueGroup && this.valueGroup ||
            changes.valuePartner && this.valuePartner) {
            this.setValues();
        }
    }

    ngOnInit(): void {
        this.getTenants();
    }

    getTenants() {
        this.datastoreResourceService
            .getAllDatastoresUsingGET()
            .subscribe((res) => {
                this.datastores = res;
                if (this.ignoreStorage || !this.saveStorage) {
                    this.setValues();
                } else {
                    this.getActualFromStorage();
                }
            });
    }

    private setValues() {
        if (this.valueGroup && this.datastores) {
            const foundGroup = this.datastores.find(d => d.code === this.valueGroup);
            if (foundGroup && foundGroup !== this.group) {
                this.group = foundGroup;
                if (this.isMultiplePartner && this.valuePartner) {
                    const foundPartner = this.group.partners.filter(p => (this.valuePartner as string[]).includes(p.code));
                    this.partner = foundPartner;
                } else if (!this.isMultiplePartner && this.valuePartner) {
                    const foundPartner = this.group.partners.find(p => p.code === (this.valuePartner as string));
                    this.partner = foundPartner;
                }
                this.onGroupChange.emit(this.group);
                this.valueGroupChange.emit(this.group.code);
                if (this.isMultiplePartner && this.partner) {
                    this.valuePartnerChange.emit((this.partner as Partner[]).map(p => p.code));
                } else if (!this.isMultiplePartner && this.partner) {
                    this.valuePartnerChange.emit((this.partner as Partner).code);
                }
                this.onPartnerChange.emit(this.partner);
                this.onChange.emit({ partner: this.partner, group: this.group });
            }
        }
    }

    getActualFromStorage() {
        const actual = localStorage.getItem('group');
        if (actual) {
            const partnersCodes = actual.split(':');
            const groupCode = partnersCodes.shift();
            this.group = this.datastores.find((group) => group.code === groupCode);
            if (partnersCodes) {
                const partners = this.group.partners.filter((partner) => partnersCodes.includes(partner.code));
                if (this.isMultiplePartner) {
                    this.partner = partners;
                } else if (partners && partners.length >= 1) {
                    this.partner = partners[0];
                } else {
                    this.partner = null;
                }
            }
            // groupe
            this.onGroupChange.emit(this.group);
            if (this.group) {
                this.valueGroupChange.emit(this.group.code);
            } else {
                this.valueGroupChange.emit(null);
            }
            // partner
            if (this.isMultiplePartner && this.partner) {
                this.valuePartnerChange.emit((this.partner as Partner[]).map(p => p.code));
            } else if (!this.isMultiplePartner && this.partner) {
                this.valuePartnerChange.emit((this.partner as Partner).code);
            }
            this.onPartnerChange.emit(this.partner);
            // groupe + partner
            this.onChange.emit({
                partner: this.partner,
                group: this.group
            });
        } else if (this.valueGroup) {
            // Aucune sélection en localStorage mais un groupe est fourni en input — l'appliquer
            this.setValues();
        } else if (this.datastores?.length === 1) {
            // Un seul groupe disponible — sélection automatique
            this.group = this.datastores[0];
            this.onGroupChange.emit(this.group);
            this.valueGroupChange.emit(this.group.code);
            this.partner = null;
            this.onPartnerChange.emit(null);
            this.valuePartnerChange.emit(null);
            this.onChange.emit({ partner: null, group: this.group });
            if (this.saveStorage) {
                this.saveActualToStorage();
            }
        } else {
            this.onChange.emit({
                partner: this.partner,
                group: this.group
            });
        }
    }

    saveActualToStorage() {
        if (this.group) {
            let key = this.group.code + ':';
            if (this.partner && this.isMultiplePartner) {
                key += (this.partner as Partner[]).map(p => p.code).join(':');
            } else if (this.partner && !this.isMultiplePartner) {
                key += (this.partner as Partner).code;
            }
            localStorage.setItem('group', key);
        } else {
            localStorage.removeItem('group');
        }
    }

    handleGroupChange() {
        // groupe
        this.onGroupChange.emit(this.group);
        if (this.group) {
            this.valueGroupChange.emit(this.group.code);
        } else {
            this.valueGroupChange.emit(null);
        }
        // partner
        this.partner = null;
        this.onPartnerChange.emit(this.partner);
        if (this.isMultiplePartner) {
            this.valuePartnerChange.emit([]);
        } else {
            this.valuePartnerChange.emit(null);
        }
        // grouper + partner
        this.onChange.emit({
            group: this.group,
            partner: this.partner
        });
        if (this.saveStorage) {
            this.saveActualToStorage();
        }
    }

    handlePartnerChange() {
        // partner
        if (this.isMultiplePartner) {
            const codes = this.partner ? (this.partner as Partner[]).map(p => p.code) : [];
            this.valuePartnerChange.emit(codes);
        } else {
            const code = this.partner ? (this.partner as Partner).code : null;
            this.valuePartnerChange.emit(code);
        }
        this.onPartnerChange.emit(this.partner);
        this.onChange.emit({
            partner: this.partner,
            group: this.group
        });
        if (this.saveStorage) {
            this.saveActualToStorage();
        }
    }
}
